import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LuChevronDown } from "react-icons/lu";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  RiAiGenerate2,
  RiFileImageLine,
  RiGeminiLine,
  RiMegaphoneLine,
  RiOpenaiLine,
} from "@remixicon/react";
import { VideoType } from "../../types";

interface VideoTypeOption {
  value: VideoType;
  label: string;
  icon: React.ReactNode;
}

interface Props {
  value: VideoType;
  onChange: (value: VideoType) => void;
}

const VIDEO_TYPE_OPTIONS: VideoTypeOption[] = [
  { value: "ugc_mirage", label: "UGC", icon: <RiAiGenerate2 size={14} /> },
  { value: "ugc_veo", label: "UGC 1.5", icon: <RiAiGenerate2 size={14} /> },
  { value: "text_overlay", label: "Text Overlay", icon: <RiFileImageLine size={14} /> },
  { value: "sora_2", label: "Sora 2", icon: <RiOpenaiLine size={14} /> },
  { value: "veo_3", label: "Veo 3", icon: <RiGeminiLine size={14} /> },
  { value: "add_hook", label: "Add Hook", icon: <RiMegaphoneLine size={14} /> },
];

const VideoTypeDropdown: React.FC<Props> = ({ value, onChange }) => {
  const selectedOption =
    VIDEO_TYPE_OPTIONS.find((opt) => opt.value === value) || VIDEO_TYPE_OPTIONS[0];
  const addHookTooltipText = "Upload a 9:16 video to add a 4‑second intro hook.";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-[6px] border-[1px]">
          <div className="text-brand-primary-400 shrink-0">{selectedOption.icon}</div>
          <span className="text-brand-primary-800 text-[12px]">
            {selectedOption.label}
          </span>
          <LuChevronDown className="h-4 w-4 text-brand-primary-new shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="p-1 bg-white shadow-none rounded-[12px] border-0 squircle squircle-radius-[12px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray"
      >
        <TooltipProvider delayDuration={250}>
          {VIDEO_TYPE_OPTIONS.map((opt) => {
            const item = (
              <DropdownMenuItem
                key={opt.value}
                className={cn(
                  "flex gap-2 h-[28px] px-2 py-1 items-center self-stretch cursor-pointer text-brand-primary-800 hover:text-brand-primary-800 text-[12px]",
                  opt.value === value ? "bg-selected-gray" : "hover:bg-hover-gray",
                )}
                onClick={() => onChange(opt.value)}
              >
                <div className="text-brand-primary-400 shrink-0">{opt.icon}</div>
                <span>{opt.label}</span>
              </DropdownMenuItem>
            );

            if (opt.value !== "add_hook") return item;

            // Tooltip for Add Hook option
            return (
              <Tooltip key={opt.value}>
                <TooltipTrigger asChild>{item}</TooltipTrigger>
                <TooltipContent side="right" align="center">
                  {addHookTooltipText}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default VideoTypeDropdown;
